import type { Router } from 'vue-router';

/**
 * Google Analytics 4 (gtag.js).
 *
 * - Measurement id comes from the server at runtime (`GACode` in /api/spa-configuration).
 * - gtag.js is loaded ONLY after the user grants analytics consent (cookie banner);
 *   withdrawing consent disables collection (`ga-disable-<id>`).
 * - SPA page views are sent on every route change (automatic page_view is off).
 */

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    [key: `ga-disable-${string}`]: boolean | undefined;
  }
}

let measurementId: string | null = null;
let loaded = false;
let enabled = false;
let lastPath: string | null = null;

function gtag(...args: unknown[]): void {
  window.gtag?.(...args);
}

function load(id: string): void {
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtagShim() {
    // gtag.js expects the `arguments` object itself, not an array.
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  gtag('js', new Date());
  gtag('config', id, { send_page_view: false, anonymize_ip: true });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(script);
  loaded = true;
}

function sendPageView(path: string): void {
  if (!enabled || !measurementId) return;
  gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.origin + path,
    page_title: document.title,
  });
}

/** Call once at startup with the runtime id and the stored consent. */
export function initAnalytics(id: string | undefined, consent: boolean): void {
  measurementId = id?.trim() || null;
  setAnalyticsConsent(consent);
}

/** Called whenever the user's analytics consent changes. */
export function setAnalyticsConsent(granted: boolean): void {
  if (!measurementId) return;
  window[`ga-disable-${measurementId}`] = !granted;
  enabled = granted;
  if (granted && !loaded) {
    load(measurementId);
    if (lastPath) sendPageView(lastPath);
  }
}

export function trackAnalyticsRouter(router: Router): void {
  router.afterEach((to, from, failure) => {
    if (failure || (from.path === to.path && from.name !== undefined)) return;
    lastPath = to.path;
    sendPageView(to.path);
  });
}

/** Custom event (no-op without consent). */
export function trackEvent(name: string, params?: Record<string, unknown>): void {
  if (!enabled) return;
  gtag('event', name, params);
}
